import React, { useEffect, useState } from "react";
import {
  Users,
  Search,
  Filter,
  Phone,
  Mail,
  MapPin,
  Calendar,
  User,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchCustomers } from "../../store/slices/customersSlice";
import {
  fetchStates,
  fetchDistrictsByState,
  fetchTalukasByDistrict,
} from "../../store/slices/commonApiSlice";
import Loader from "../../components/Loader";
import Pagination from "../../components/tables/Pagination";

export default function ViewCustomersPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState("");
  const [filterGender, setFilterGender] = useState("all");
  const [filterState, setFilterState] = useState("all");
  const [filterDistrict, setFilterDistrict] = useState("all");
  const [filterTaluka, setFilterTaluka] = useState("all");

  const {
    customers = [],
    pagination,
    loading,
  } = useSelector((state) => state.customers);

  const {
    states = [],
    districts = [],
    talukas = [],
  } = useSelector((state) => state.common);

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const itemsPerPage = 10;

  // Calculate which backend page we need based on current frontend page
  const backendPageSize = 100;
  const startRecord = (currentPage - 1) * itemsPerPage;
  const backendPage = Math.floor(startRecord / backendPageSize) + 1;

  useEffect(() => {
    // Fetch the backend page that contains the records we need
    dispatch(fetchCustomers({ page: backendPage, per_page: backendPageSize }));
    dispatch(fetchStates());
  }, [dispatch, backendPage]);

  // Check if any filters are active
  const hasActiveFilters = 
    searchTerm !== "" ||
    filterGender !== "all" ||
    filterState !== "all" ||
    filterDistrict !== "all" ||
    filterTaluka !== "all";

  const filteredCustomers = customers.filter((customer) => {
    const matchesSearch =
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.mobile.includes(searchTerm);
    const matchesFilter =
      filterGender === "all" ||
      customer.gender.toLowerCase() === filterGender.toLowerCase();
    const matchesState =
      filterState === "all" || customer.state?.uuid === filterState;
    const matchesDistrict =
      filterDistrict === "all" || customer.district?.uuid === filterDistrict;
    const matchesTaluka =
      filterTaluka === "all" || customer.taluka?.uuid === filterTaluka;
    return matchesSearch && matchesFilter && matchesState && matchesDistrict && matchesTaluka;
  });

  // Client-side pagination from backend data
  // Use filtered count when filters are active so page count is accurate
  const totalRecords = hasActiveFilters
    ? filteredCustomers.length
    : (pagination?.total || filteredCustomers.length);
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  
  // Calculate which records to show from the current backend page
  const backendStartIndex = (backendPage - 1) * backendPageSize;
  const localStartIndex = startRecord - backendStartIndex;
  const localEndIndex = localStartIndex + itemsPerPage;
  const paginatedCustomers = filteredCustomers.slice(localStartIndex, localEndIndex);

  const handlePageChange = (page) => {
    navigate(`?page=${page}`);
  };

  // Handle state change to fetch districts
  const handleStateChange = (stateUuid) => {
    setFilterState(stateUuid);
    setFilterDistrict("all");
    setFilterTaluka("all");
    if (stateUuid !== "all") {
      dispatch(fetchDistrictsByState(stateUuid));
    }
  };

  // Handle district change to fetch talukas
  const handleDistrictChange = (districtUuid) => {
    setFilterDistrict(districtUuid);
    setFilterTaluka("all");
    if (districtUuid !== "all") {
      dispatch(fetchTalukasByDistrict(districtUuid));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not provided";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {loading && <Loader />}

      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">All Customers</h2>
            <p className="text-gray-600 mt-1">
              Manage and view customer information
            </p>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search customers by name, email, or mobile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <select
                value={filterGender}
                onChange={(e) => setFilterGender(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>

          {/* Location Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* State Filter */}
            <div className="relative">
              <select
                value={filterState}
                onChange={(e) => handleStateChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none"
              >
                <option value="all">All States</option>
                {states.map((state) => (
                  <option key={state.uuid} value={state.uuid}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            {/* District Filter */}
            <div className="relative">
              <select
                value={filterDistrict}
                onChange={(e) => handleDistrictChange(e.target.value)}
                disabled={filterState === "all"}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="all">All Districts</option>
                {districts.map((district) => (
                  <option key={district.uuid} value={district.uuid}>
                    {district.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Taluka Filter */}
            <div className="relative">
              <select
                value={filterTaluka}
                onChange={(e) => setFilterTaluka(e.target.value)}
                disabled={filterDistrict === "all"}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="all">All Talukas</option>
                {talukas.map((taluka) => (
                  <option key={taluka.uuid} value={taluka.uuid}>
                    {taluka.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Location
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Gender
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date of Birth
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Joined
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedCustomers.length > 0 ? (
                paginatedCustomers.map((customer) => (
                  <tr key={customer.uuid} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <User className="h-5 w-5 text-blue-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {customer.name ?? "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm text-gray-900">
                          <Phone className="h-4 w-4 mr-2 text-gray-400" />
                          {customer.mobile ?? "N/A"}
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Mail className="h-4 w-4 mr-2 text-gray-400" />
                          {customer.email ?? "N/A"}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm text-gray-900">
                          <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                          {customer.state?.name || "N/A"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {customer.district?.name || "N/A"}, {customer.taluka?.name || "N/A"}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        customer.gender ? (
                          customer.gender.toLowerCase() === 'male' 
                            ? 'bg-blue-100 text-blue-800'
                            : customer.gender.toLowerCase() === 'female'
                            ? 'bg-pink-100 text-pink-800'
                            : 'bg-gray-100 text-gray-800'
                        ) : 'bg-gray-100 text-gray-800'
                      }`}>
                        {customer.gender ?? "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center text-sm text-gray-900">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        {formatDate(customer.date_of_birth)}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(customer.created_at)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    <Users className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                    <p className="text-lg font-medium mb-2">No customers found</p>
                    <p className="text-sm">
                      {searchTerm || filterGender !== "all"
                        ? "Try adjusting your search or filter criteria"
                        : "No customers have registered yet"}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Filter Warning */}
      {hasActiveFilters && totalPages > 1 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <p className="text-sm text-yellow-800">
            <strong>Note:</strong> Filters are applied to all data. 
            Showing {paginatedCustomers.length} of {filteredCustomers.length} filtered results on this page.
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalRecords}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      )}
    </div>
  );
}